import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import usePublisher from 'api/usePublisher';
import { IntegrationDomain, IntegrationVersion } from 'api/usePublisherIntegrationTest';
import { useUserDetails } from 'api/useUserDetails';
import { PublisherInfo } from 'common/contracts';
import { EEventsType } from 'constants/enums';
import { BigQueryEventData, useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { trackMixpanelEvent } from 'utils/mixpanelClient';

import { ESaveResultStatus, ISaveResult } from './useCallbackForms';

interface IWithUrl {
  url: string;
}

interface ITrackTestExecutedParams extends IWithUrl {
  statusCode: number;
}

interface ITrackIntegrationSavedParams extends IWithUrl {
  status: ESaveResultStatus;
  api: string;
  version: IntegrationVersion;
}

interface IUseApiTestingAnalyticsParams {
  domain: IntegrationDomain;
  version: IntegrationVersion;
}

const ANALYTICS_TAB = 'settings';
const ANALYTICS_PAGE = 'developer settings';

export const useApiTestingAnalytics = ({
  domain,
  version
}: IUseApiTestingAnalyticsParams) => {
  const { publisherId } = useParams<{ publisherId: string }>();
  const { userDetails } = useUserDetails();
  const { getPublisher } = usePublisher(publisherId || undefined);
  const { trackBigQueryEvent } = useBigQueryEventsTracking();

  const publisherData = getPublisher?.data as PublisherInfo | undefined;

  return useMemo(() => {
    const bigQueryBase: Partial<BigQueryEventData> = {
      publisherId: publisherId || '',
      tab: ANALYTICS_TAB,
      page: ANALYTICS_PAGE,
      api: domain,
      version,
      role: userDetails?.role,
      businessName: publisherData?.companyName,
      solution: publisherData?.publisherSolutionModel,
      solutionSubtype: publisherData?.solutionModelSubtype
    };

    const mixpanelBase: Record<string, unknown> = {
      publisher_id: publisherId || '',
      user_id: userDetails?.userId,
      user_email: userDetails?.email,
      tab: ANALYTICS_TAB,
      page: ANALYTICS_PAGE,
      api: domain,
      version,
      user_role: userDetails?.role,
      business_name: publisherData?.companyName,
      solution: publisherData?.publisherSolutionModel,
      solution_subtype: publisherData?.solutionModelSubtype
    };

    const trackEvent = (eventName: EEventsType, extra?: Record<string, unknown>) => {
      trackBigQueryEvent({ eventName, ...bigQueryBase, ...extra });
      trackMixpanelEvent(eventName, { ...mixpanelBase, ...extra });
    };

    const trackTestModalOpened = ({ url }: IWithUrl) => {
      trackEvent(EEventsType.DASHBOARD_API_TEST_OPEN_MODAL, { url });
    };

    const trackTestExecuted = ({ statusCode, url }: ITrackTestExecutedParams) => {
      trackBigQueryEvent({
        eventName: EEventsType.DASHBOARD_API_TEST_CLICKED,
        ...bigQueryBase,
        responseCode: statusCode,
        url
      });
      trackMixpanelEvent(EEventsType.DASHBOARD_API_TEST_CLICKED, {
        ...mixpanelBase,
        response_code: statusCode,
        url
      });
    };

    const trackIntegrationSaved = ({
      url,
      status,
      api,
      version: savedVersion
    }: ITrackIntegrationSavedParams) => {
      trackEvent(EEventsType.DASHBOARD_API_INTEGRATION_SAVED, {
        api,
        version: savedVersion,
        url,
        status
      });
    };

    const trackDocsClicked = (docsUrl: string) => {
      trackEvent(EEventsType.DASHBOARD_API_DOCS_CLICKED, { url: docsUrl });
    };

    const handleSaveResult = ({ status, url }: ISaveResult) => {
      trackIntegrationSaved({ url, status, api: domain, version });
    };

    return {
      trackIntegrationSaved,
      trackTestModalOpened,
      trackTestExecuted,
      trackDocsClicked,
      handleSaveResult
    };
  }, [
    publisherId,
    domain,
    version,
    userDetails?.role,
    userDetails?.userId,
    userDetails?.email,
    publisherData?.companyName,
    publisherData?.publisherSolutionModel,
    publisherData?.solutionModelSubtype,
    trackBigQueryEvent
  ]);
};
